import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, AlertCircle, Calendar, TrendingUp } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

const notificationOptions = [
  {
    id: "budget_exceeded",
    label: "Budsjettoverskridelser",
    description: "Få varsling når et budsjett blir overskredet",
    icon: AlertCircle,
    color: "text-red-600",
    bgColor: "bg-red-50",
  },
  {
    id: "followup_reminders",
    label: "Oppfølgingspåminnelser",
    description: "Påminnelser om investorer som trenger oppfølging",
    icon: Bell,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
  },
  {
    id: "task_deadlines",
    label: "Oppgavefrister",
    description: "Varsling om kommende eller forfalte oppgavefrister",
    icon: Calendar,
    color: "text-zinc-900",
    bgColor: "bg-lime-100",
  },
  {
    id: "investor_updates",
    label: "Investoroppdateringer",
    description: "Viktige oppdateringer og endringer på investorer",
    icon: TrendingUp,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
  },
];

export default function NotificationPreferences({ user }) {
  const [preferences, setPreferences] = useState({
    budget_exceeded: true,
    followup_reminders: true,
    task_deadlines: true,
    investor_updates: true,
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (user?.notification_preferences) {
      setPreferences({
        budget_exceeded: user.notification_preferences.budget_exceeded ?? true,
        followup_reminders: user.notification_preferences.followup_reminders ?? true,
        task_deadlines: user.notification_preferences.task_deadlines ?? true,
        investor_updates: user.notification_preferences.investor_updates ?? true,
      });
    }
  }, [user]);

  const updatePreferencesMutation = useMutation({
    mutationFn: (data) => base44.auth.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      toast.success("Varslingspreferanser oppdatert");
    },
  });

  const handleToggle = (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key],
    };
    setPreferences(newPreferences);
  };

  const handleSave = () => {
    updatePreferencesMutation.mutate({
      notification_preferences: preferences,
    });
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
          <Bell className="w-5 h-5 text-zinc-900" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Varslingspreferanser</h3>
          <p className="text-sm text-slate-500">Velg hvilke varsler du vil motta</p>
        </div>
      </div>

      <div className="space-y-4">
        {notificationOptions.map((option) => {
          const Icon = option.icon;
          return (
            <div
              key={option.id}
              className="flex items-start justify-between p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start gap-3 flex-1">
                <div className={`p-2 rounded-lg ${option.bgColor} mt-0.5`}>
                  <Icon className={`w-4 h-4 ${option.color}`} />
                </div>
                <div className="flex-1">
                  <Label htmlFor={option.id} className="font-medium text-slate-900 cursor-pointer">
                    {option.label}
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">{option.description}</p>
                </div>
              </div>
              <Switch
                id={option.id}
                checked={preferences[option.id]}
                onCheckedChange={() => handleToggle(option.id)}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-6 mt-6 border-t border-slate-200">
        <p className="text-sm text-slate-500">
          Endringer lagres automatisk i systemet
        </p>
        <Button
          onClick={handleSave}
          disabled={updatePreferencesMutation.isPending}
          className="bg-slate-900 hover:bg-slate-800"
        >
          {updatePreferencesMutation.isPending ? "Lagrer..." : "Lagre innstillinger"}
        </Button>
      </div>
    </Card>
  );
}